import { observer } from 'mobx-react';
import React from 'react';
import { translator, i18n } from 'core/localization';
import { Section } from 'controls/react/layout/section';
import { Toolbar, ToolbarItemPosition } from 'controls/react/layout/toolbar';
import { AntAlert } from 'controls/react/ant/antAlert';
import { FormEntryNew, HelpIcon } from 'controls/react/form/formEntryNew';
import { UserConfigurationStore } from './userConfigurationStore';
import { AntInput } from 'controls/react/ant/antInput';
import { Col, Flex, Row } from 'antd';
import { TimeZoneSelector } from 'controls/react/TimeZoneSelector';
import { TeamSelector } from 'controls/react/teamSelector';
import { AntCheckbox } from 'controls/react/ant/antCheckbox';
import { linkModel } from 'framework/mobx-integration';
import { TagsSelect } from 'controls/react/tagsSelect';
import { AntSwitch } from 'controls/react/ant/antSwitch';
import { LandingPagesSelector } from '../components/landingPagesSelector';
import { ActionTriggerSwitcher } from '../components/action-trigger-switcher/actionTriggerSwitcher';
import { LocaleSelector } from 'controls/react/localeSelector';
import { DateFormatSelector, DateTimeFormatSelector, TimeFormatSelector } from 'controls/react/dateTimeFormatSelector';
import { RoleSelector } from '../components/roleSelector';
import { LoginAccountSelector } from '../components/login-account-selector/loginAccountSelector';
import { ClearUserSettings } from '../components/clear-user-settings/clearUserSettings';

const i = translator({
	'You are not allowed to change this setting for yourself': {
		no: 'Du har ikke lov til å endre denne innstillingen for din bruker',
		en: 'You are not allowed to change this setting for your user',
	},
	'General': { no: 'Generelt' },
	'Login account': { no: 'Innloggingskonto' },
	'Home view': { no: 'Hjem visning' },
	'Phone': { no: 'Telefon' },
	'Language and time': { no: 'Språk og tid' },
	'Language': { no: 'Språk' },
	'Timezone': { no: 'Tidssone' },
	'Date time': { no: 'Datotid' },
	'Date': { no: 'Dato' },
	'Time': { no: 'Tid' },
	'Team settings': { no: 'Team innstillinger' },
	'Teams': { no: 'Grupper' },
	'Force team tags': { no: 'Påtving teametiketter' },
	'Forced tags': { no: 'Låste etiketter' },
	'Active': { no: 'Aktiv' },
	'Forced tags are inactive for this user': { no: 'Tvungne etiketter er inaktive for denne brukeren' },
	'FORCE_TAGS_HELP_MESSAGE1': {
		en: 'Force Tags automatically applies a specific set of tags to a user to filter their Ceeview content.',
		no: 'Tving etiketter tildeler automatisk et utvalg etiketter til en bruker for å filtrere Ceeview-innhold.',
	},
	'FORCE_TAGS_HELP_MESSAGE2': {
		en: 'Users cannot edit these forced tags themselves.',
		no: 'Brukeren kan ikke endre disse selv.',
	},
	'Password': { no: 'Passord' },
	'Active user': { no: 'Aktiv bruker' },
	'Change': { no: 'Endre' },
	'Generate': { no: 'Generere' },
	'User must change password next time she logs in.': { no: 'Brukeren må endre passord ved neste login.' },
	'Ceeview will create a new password. Copy it and pass in a secure manner to user.': {
		no: 'Ceeview vil lage et nytt passord. Kopier og gi til brukeren på en sikker måte.',
	},
	'Current password': { no: 'Gjeldende passord' },
	'Confirm password': { no: 'Bekreft passord' },
	'Ceeview system administrator': { no: 'Ceeview systemadministrator' },
	'Advanced': { no: 'Avansert' },
	'Type': { no: 'Type' },
	'Externally managed': { no: 'Eksternt administrert' },
	'User attributes controlled via SSO profile.': { no: 'Brukerattributter styres via SSO profil.' },
	'This setting is read only and informational only.': {
		no: 'Dette feltet er skrivebeskyttet og kun for informasjon.',
	},
});

export const UserConfiguration = observer(({ store }: { store: UserConfigurationStore }) => {
	const validator = store.validator;
	const forbiddenForSelf = store.isCurrentUser ? i('You are not allowed to change this setting for yourself') : '';

	return (
		<Section appearance="none" layout="equal" direction="row" margin="top">
			<Section appearance="none">
				<Section appearance="frame-top-only" title={i('General')} childrenPadding={true}>
					<FormEntryNew
						label={i('Username')}
						model={store.user}
						modelField={'username'}
						validator={validator}
						disabled={store.disabled || !store.isCreateMode}
					>
						<AntInput />
					</FormEntryNew>
					<FormEntryNew
						label={i('Login account')}
						model={store.user}
						modelField={'loginAccountId'}
						disabled={store.disabled}
					>
						<LoginAccountSelector accountId={store.accountContext} />
					</FormEntryNew>
					<FormEntryNew
						label={i('Home view')}
						model={store.user}
						modelField={'landingPages'}
						disabled={store.disabled}
					>
						<LandingPagesSelector accountId={store.accountContext} />
					</FormEntryNew>
					<FormEntryNew
						label={i('First name')}
						model={store.user}
						modelField={'firstName'}
						validator={validator}
						disabled={store.disabled}
					>
						<AntInput />
					</FormEntryNew>
					<FormEntryNew
						label={i('Last name')}
						model={store.user}
						modelField={'lastName'}
						validator={validator}
						disabled={store.disabled}
					>
						<AntInput />
					</FormEntryNew>
					<FormEntryNew
						label={i('Email')}
						model={store.user}
						modelField={'email'}
						validator={validator}
						disabled={store.disabled}
					>
						<AntInput />
					</FormEntryNew>
					<FormEntryNew
						label={i('Phone')}
						model={store.user}
						modelField={'phoneNumber'}
						disabled={store.disabled}
					>
						<AntInput />
					</FormEntryNew>
					<ActionTriggerSwitcher type={'users'} id={store.user.id} disabled={store.disabled} />
				</Section>
			</Section>
			<Section appearance="none">
				<Section appearance="frame-top-only" title={i('Language and time')} childrenPadding={true}>
					<FormEntryNew
						label={i('Language')}
						model={store.user}
						modelField={'locale'}
						validator={validator}
						disabled={store.disabled}
					>
						<LocaleSelector />
					</FormEntryNew>
					<FormEntryNew
						label={i('Timezone')}
						model={store.user}
						modelField={'timeZone'}
						validator={validator}
						disabled={store.disabled}
					>
						<TimeZoneSelector />
					</FormEntryNew>
					<FormEntryNew
						label={i('Date time')}
						model={store.user}
						modelField={'dateTimeFormat'}
						disabled={store.disabled}
					>
						<DateTimeFormatSelector />
					</FormEntryNew>
					<FormEntryNew
						label={i('Date')}
						model={store.user}
						modelField={'dateFormat'}
						disabled={store.disabled}
					>
						<DateFormatSelector />
					</FormEntryNew>
					<FormEntryNew
						label={i('Time')}
						model={store.user}
						modelField={'timeFormat'}
						disabled={store.disabled}
					>
						<TimeFormatSelector />
					</FormEntryNew>
				</Section>
				<Section appearance="frame-top-only" title={i('Team settings')} childrenPadding={true}>
					<FormEntryNew label={i('Teams')} model={store.user} modelField={'teams'} disabled={store.disabled}>
						<TeamSelector multiple={true} accountId={store.accountContext} />
					</FormEntryNew>
					<AntCheckbox
						{...linkModel(store.user, 'forceTeamTags')}
						disabled={store.disabled || store.isCurrentUser}
						title={forbiddenForSelf}
					>
						{i('Force team tags')}
					</AntCheckbox>
				</Section>
				<Section appearance="frame-top-only" title={i('Forced tags')}>
					<Toolbar>
						<HelpIcon tooltip={i('FORCE_TAGS_HELP_MESSAGE1') + '\n' + i('FORCE_TAGS_HELP_MESSAGE2')} />
						<AntSwitch
							{...linkModel(store.user, 'forceUserTags')}
							position={ToolbarItemPosition.AT_THE_END}
							size={'small'}
							checkedChildren={i('Active')}
							unCheckedChildren={i('Active')}
							disabled={store.disabled || store.isCurrentUser}
						/>
					</Toolbar>
					{!store.user.forceUserTags && (
						<AntAlert type={'info'} title={i('Forced tags are inactive for this user')} />
					)}
					{store.user.forceUserTags && (
						<FormEntryNew
							label={i('Tags')}
							model={store.user}
							modelField={'tags'}
							disabled={store.disabled || store.isCurrentUser}
						>
							<TagsSelect
								accountId={store.accountContext}
								includeSubaccounts={true}
								placeholder={i('Tags')}
							/>
						</FormEntryNew>
					)}
				</Section>
				<Section appearance="frame-top-only" title={i('Role')} childrenPadding={true}>
					{store.isAdminUser ? (
						<AntAlert type={'info'} title={i('Ceeview system administrator')} />
					) : (
						<FormEntryNew
							label={i('Role')}
							model={store.user}
							modelField={'userCategoryId'}
							validator={validator}
							disabled={store.disabled || store.isCurrentUser || undefined}
						>
							<RoleSelector accountId={store.accountContext} placeholder={i18n('Select role...')} />
						</FormEntryNew>
					)}
				</Section>
			</Section>
			<Section appearance="none">
				<Section appearance="frame-top-only" title={i('Password')} childrenPadding={true}>
					{store.showActiveUserSwitch && (
						<Flex align="center" justify="flex-start" gap={20}>
							<span>{i('Active user')}&nbsp;</span>
							<AntSwitch
								{...linkModel(store, 'activeUser')}
								disabled={store.disabled || store.isCreateMode}
							/>
						</Flex>
					)}
					{store.showPasswordFlags && (
						<Flex align="center" justify="space-between" gap={20}>
							<Flex align="center" justify="flex-start" gap={20}>
								<span>
									{i('Change')}&nbsp;
									<HelpIcon tooltip={i('User must change password next time she logs in.')} />
								</span>
								<AntSwitch
									{...linkModel(store.user, 'changePassword')}
									disabled={store.disabled || store.isLdapUser || !!store.user.lockUser}
								/>
							</Flex>
							<Flex align="center" justify="flex-start" gap={20}>
								<span>
									{i('Generate')}&nbsp;
									<HelpIcon
										tooltip={i(
											'Ceeview will create a new password. Copy it and pass in a secure manner to user.',
										)}
									/>
								</span>
								<AntSwitch
									{...linkModel(store, 'generatePassword')}
									disabled={store.disabled || store.isLdapUser}
								/>
							</Flex>
						</Flex>
					)}
					{store.showPasswordFields && (
						<>
							{store.showOldPassword && !store.isCreateMode && (
								<FormEntryNew
									label={i('Current password')}
									model={store.user}
									modelField={'oldPassword'}
									validator={validator}
									disabled={store.disabled}
								>
									<AntInput />
								</FormEntryNew>
							)}
							<FormEntryNew
								label={i('Password')}
								model={store.user}
								modelField={'password'}
								validator={validator}
								disabled={store.disabled}
							>
								<AntInput readOnly={store.passwordReadOnly} />
							</FormEntryNew>
							<FormEntryNew
								label={i('Confirm password')}
								model={store.user}
								modelField={'passwordConfirm'}
								validator={validator}
								disabled={store.disabled}
							>
								<AntInput readOnly={store.passwordReadOnly} />
							</FormEntryNew>
							{store.isLdapUser && (
								<span className="cw_info">* {i18n('LDAP type user must handle password in AD')}</span>
							)}
						</>
					)}
				</Section>
				{!store.isCreateMode && (
					<Section appearance="frame-top-only" title={i('Advanced')} childrenPadding={true}>
						<FormEntryNew label={i('Type')} model={store.user} modelField={'authType'} disabled={true}>
							<AntInput />
						</FormEntryNew>
						<AntCheckbox {...linkModel(store.user, 'externallyManaged')} disabled={true}>
							{i('Externally managed')}
							<HelpIcon
								tooltip={`${i('User attributes controlled via SSO profile.')} ${i('This setting is read only and informational only.')}`}
							/>
						</AntCheckbox>
						<FormEntryNew label={i('CVID')} model={store.user} modelField={'id'} disabled={true}>
							<AntInput />
						</FormEntryNew>
					</Section>
				)}
				{store.isCurrentUser && <ClearUserSettings />}
			</Section>
		</Section>
	);
});
